/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

function waitForElm(selector) {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector(selector));
    }

    const observer = new MutationObserver((mutations) => {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
}

document.addEventListener("on:quickbuy:after-open", (event) => {
  document.body.classList.add("quickbuy-open");
  let popupATCButton = document.querySelector(
    '.quickbuy-container__card .product-form .quantity-submit-row .quantity-submit-row__submit button[type="submit"]'
  );
  popupATCButton.addEventListener("click", () => {
    setTimeout(() => {
      popupATCButton
        .closest(".quickbuy-container")
        .classList.remove("quickbuy-container--expanded");
      document.body.classList.remove("quickbuy-open");
    }, 500);
  });
});

document.addEventListener("on:quickbuy:after-close", (event) => {
  document.body.classList.remove("quickbuy-open");
});

const getCookie = (name) => {
  let value = `; ${document.cookie}`;
  let parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

const updateShippingBar = async (selector, cart = false) => {
  let formattedSelector = selector.indexOf("announcement") > -1 ? selector : `${selector}-text`;
  let shippingReached = document.querySelector(`${formattedSelector}[data-reached]`);
  let shippingNotReached = document.querySelector(
    `${formattedSelector}[data-not-reached]`
  );
  let country;

  let localisationCookie = getCookie("localization");
  if (localisationCookie) {
    country = localisationCookie;
  }

  if (!country) {
    return;
    // let countryResponse = await fetch(
    //   "https://ipapi.co/json/?key=EQM4a2sY1rRUnfR855Mvou08TWQEtSGpA3uBYZ2DoqcnqzlVtN"
    // ).then((response) => response.json());
    // country = countryResponse.country;
  }

  cart = !!cart
    ? cart
    : await fetch("/cart.js").then((response) => response.json());
  let threshold = window.shippingThresholds[country];

  if (!threshold) {
    // Show fallback text when no threshold exists for the country
    let shippingFallback = document.querySelector(
      `${formattedSelector}[data-fallback]`
    );
    if (shippingReached) shippingReached.classList.add("hidden");
    if (shippingNotReached) shippingNotReached.classList.add("hidden");
    if (shippingFallback) shippingFallback.classList.remove("hidden");
    return;
  }

  let shippingCost = threshold - cart.total_price / 100;
  shippingCost = Math.round(shippingCost);
  if (shippingCost > 0) {
    let shippingCostFormatted = theme.settings.moneyWithCurrencyFormat.replace(
      /\{\{\s*(\w+)\s*\}\}/,
      shippingCost
    );
    shippingReached.classList.add("hidden");
    shippingNotReached.classList.remove("hidden");

    if (shippingNotReached.innerHTML.includes("{shippingCost}")) {
      shippingNotReached.innerHTML = shippingNotReached.innerHTML.replace(
        "{shippingCost}",
        `<span>${shippingCostFormatted}</span>`
      );
    } else {
      let shippingCostSpan = shippingNotReached.querySelector("span");
      if (shippingCostSpan) {
        shippingCostSpan.innerHTML = shippingCostFormatted;
      }
    }
  } else {
    shippingReached.classList.remove("hidden");
    shippingNotReached.classList.add("hidden");
  }

  let shippingBar = document.querySelector(`${selector}-bar__fill`);
  let shippingBarWidth = (cart.total_price / 100 / threshold) * 100;
  if (!shippingBar) return;

  shippingBar.style.setProperty("--shipping-progress", `${shippingBarWidth}%`);
};

const updateShippingBars = async (cart = false) => {
  let selectors = [".announcement__shipping", ".cart-info__shipping"];
  selectors.map((selector) => {
    updateShippingBar(selector, cart);
  });
};

const listenToCart = (callback) => {
  const ShopifyCartURLs = ["cart.json"];

  const restockSignupURL =
    "https://alertmerestockalerts.herokuapp.com/addemail";

  function isShopifyCartURL(url) {
    if (!url || typeof url != "string") return false;
    const path = url.split("/").pop().split("?")[0];
    return ShopifyCartURLs.includes(`${path}`);
  }

  function dispatchEvent(url, detail) {
    callback(detail);
  }

  function fetchOverride() {
    if (!window.fetch || typeof window.fetch !== "function") return;
    const originalFetch = window.fetch;
    window.fetch = function () {
      const response = originalFetch.apply(this, arguments);
      let url = arguments[0];
      if (typeof url === "object") {
        url = url.href;
      }
      if (isShopifyCartURL(url)) {
        response.then((res) => {
          res
            .clone()
            .json()
            .then((data) => dispatchEvent(res.url, data));
        });
      } else if (url.includes(restockSignupURL)) {
        let restockPopup = document.querySelector("#restock-section");
        setTimeout(() => {
          restockPopup.classList.remove("restock-section--expanded");
        }, 2500);
      }

      return response;
    };
  }

  function XHROverride() {
    if (!window.XMLHttpRequest) return;

    const originalOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function () {
      const url = arguments[1];
      this.addEventListener("load", function () {
        if (isShopifyCartURL(url)) {
          dispatchEvent(url, JSON.parse(this.response));
        } else if (url.includes(restockSignupURL)) {
          let restockPopup = document.querySelector("#restock-section");
          setTimeout(() => {
            restockPopup.classList.remove("restock-section--expanded");
          }, 2500);
        }
      });
      return originalOpen.apply(this, arguments);
    };
  }

  XHROverride();
  fetchOverride();
};

const utilityBarFixed = (utilityBar, utilityBarClone, header) => {
  if (
    utilityBar.getBoundingClientRect().top <
    header.getBoundingClientRect().height
  ) {
    utilityBar.classList.add("utility-bar--fixed");
    utilityBarClone.classList.add("utility-bar__clone--fixed");
  } else {
    utilityBar.classList.remove("utility-bar--fixed");
    utilityBarClone.classList.remove("utility-bar__clone--fixed");
  }
};

document.addEventListener("DOMContentLoaded", () => {
  updateShippingBars();

  let utilityBar = document.querySelector(".utility-bar");
  let utilityBarClone = document.querySelector(".utility-bar__clone");
  let header = document.querySelector(".section-header");

  if (!!utilityBar && !!utilityBarClone && !!header) {
    document.addEventListener("scroll", () => {
      utilityBarClone.style.setProperty(
        "--header-height",
        `${header.getBoundingClientRect().height}px`
      );
      utilityBarFixed(utilityBar, utilityBarClone, header);
    });
    utilityBarClone.style.setProperty(
      "--header-height",
      `${header.getBoundingClientRect().height}px`
    );

    utilityBarFixed(utilityBar, utilityBarClone, header);
  }

  document.addEventListener("click", (event) => {
    if (event.target.classList.contains("atc--disabled")) {
      event.preventDefault();
      let restockPopup = document.querySelector("#restock-section");
      restockPopup.classList.add("restock-section--expanded");
    }

    if (event.target.classList.contains("restock-section--expanded")) {
      event.target.classList.remove("restock-section--expanded");
    }
  });

  waitForElm(".cbb-frequently-bought-recommendations-container").then((el) => {
    let bundleSelects = document.querySelector(
      ".cbb-frequently-bought-selector-list"
    );
    el.appendChild(bundleSelects);

    let selectOptions = document.querySelectorAll(
      ".cbb-frequently-bought-selector-list select.cbb-recommendations-variant-select option"
    );
    selectOptions.forEach((option) => {
      option.innerHTML = option.innerHTML.split(" / ")[1];
    });
  });

  const addQuickAddListeners = () => {
    let quickAdds = document.querySelectorAll(
      ".product-quick-add:not(product-quick-add--single)"
    );
    quickAdds.forEach((quickAdd) => {
      let closeBtn = quickAdd
        .closest(".product-block")
        .querySelector(".quick-add__modal-close");

      quickAdd.addEventListener("click", async () => {
        let modal = quickAdd
          .closest(".product-block")
          .querySelector(".quick-add__modal");
        modal.classList.add("quick-add__modal--expanded");
        document.body.classList.add("quickbuy-open");
      });

      if (!closeBtn) return;
      closeBtn.addEventListener("click", async () => {
        let modal = closeBtn
          .closest(".product-block")
          .querySelector(".quick-add__modal");
        modal.classList.remove("quick-add__modal--expanded");
        document.body.classList.remove("quickbuy-open");
      });
    });
  };

  if (document.querySelector(".related-products")) {
    waitForElm(".related-products .collection-listing").then(
      addQuickAddListeners
    );
  } else {
    addQuickAddListeners();
  }

  let quickBuyOverride = document.querySelectorAll(
    ".product-quick-add--single"
  );
  quickBuyOverride.forEach((quickBuy) => {
    quickBuy.classList.add("quick-buy-override");
    quickBuy.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      let variantId = quickBuy.dataset.variant;
      await fetch(`/cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      }).then((response) => response.json());
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
      return false;
    });
  });

  document.addEventListener("dispatch:cart-drawer:refresh", async () => {
    let counterItem = document.querySelector(".cart-link__count");
    let cart = await fetch("/cart.js").then((response) => response.json());
    counterItem.innerHTML = cart.item_count;
  });
});

listenToCart((cart) => {
  updateShippingBars(cart);
});

function expandAllThirdLevelMenus() {
  waitForElm(".mobile-navigation-drawer").then((drawer) => {
    drawer
      .querySelectorAll(".navigation__tier-2 .navigation__item--with-children")
      .forEach((el) => {
        // Add open class
        el.classList.add("navigation__item--open");

        // Set height for expanded view
        const container = el.querySelector(".navigation__tier-3-container");
        if (container && container.firstElementChild) {
          container.style.height = `${container.firstElementChild.clientHeight}px`;
        }

        // Set aria-expanded
        const toggle = el.querySelector(".navigation__children-toggle");
        if (toggle) {
          toggle.setAttribute("aria-expanded", "true");
        }

        let title = el.querySelector(".navigation__tier-3-container.navigation__child-tier .navigation__link.navigation__column-title");
        if (title) {
          title.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            toggle.click();
          });
        }
      });
  });
}

document.addEventListener("DOMContentLoaded", () => {
  expandAllThirdLevelMenus();
});
